/**
 * landing-locale — non-component exports for the landing i18n context.
 *
 * Split out of LandingI18n.tsx so the provider file exports only a
 * component (react-refresh/only-export-components).
 */

import { createContext, useContext, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { getStrings, type StringKey, type StringTable } from '@shared/i18n'
import { splitLocalePath } from '../../lib/public-locale'

export const LandingLocaleContext = createContext<string>('en')

/** The detected landing locale ('en' | 'ko' | 'zh' | 'zh-TW' | 'ja' | …). */
export function useLandingLocale() {
  return useContext(LandingLocaleContext)
}

/** Prefix internal hrefs with the current locale (/ko/login under /ko/).
 *  Keeps public CTAs inside the same language family (seo plan M2). */
export function usePublicHref() {
  const { pathname } = useLocation()
  const { locale } = splitLocalePath(pathname)
  return (path: string) => (locale ? `/${locale}${path}` : path)
}

/** Landing-only `t` — resolves `landing.*` keys from the detected locale. */
export function useLandingT() {
  const locale = useContext(LandingLocaleContext)
  const strings = useMemo<StringTable>(() => getStrings(locale), [locale])
  const en = useMemo<StringTable>(() => getStrings('en'), [])
  const t = useMemo(() => {
    return (key: StringKey) => strings[key] ?? en[key] ?? key
  }, [strings, en])
  return t
}
